import React, { ChangeEvent, CSSProperties, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import screenImage from '../../assets/images/welcome-pattern.png';
import { appWidth } from './CSD/WelcomeCSD';
import { Heading } from '../components/Heading';
import { Router } from '../router/Router';
import { Input } from '../components/Input';
import { Separator } from '../components/Separator';
import { Button } from '../components/Button';
import { emailChecker } from '../utils/InputChecker';
import { CustomCheckBox } from '../components/CustomCheckBox';
import { DeviceInfo, Profile } from '../models/Profile';
import { Loading } from '../components/Loading';
import { API } from '../utils/API';
import { ToastBar } from '../components/ToastBar';
import { NotAuthProvider } from '../components/NotAuthProvider';

export const SignUp: React.FC = () => {
  const navigation = useNavigate();

  const styles = {
    root: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
    } as CSSProperties,
    bgImage: {
      height: '60%',
      position: 'fixed',
      bottom: 5,
      right: 0,
      zIndex: -1,
    } as CSSProperties,
    mainContainer: {
      width: '100%',
      maxWidth: 400,
      display: 'flex',
      flexDirection: 'column',
      background: 'white',
      borderRadius: '20px',
      padding: '40px 30px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      maxHeight: '90vh',
      overflowY: 'auto',
    } as CSSProperties,
    form: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    } as CSSProperties,
  };

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isToastbarVisible, setToastbarVisible] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');

  const handleBackPressed = () => Router(navigation).goToWelcome();

  const [emailError, setEmailError] = useState<boolean>(false);
  const [firstNameError, setFirstNameError] = useState<boolean>(false);
  const [secondNameError, setSecondNameError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [secondPasswordError, setSecondPasswordError] = useState<boolean>(false);

  const [smartCard, setSmartCard] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [secondName, setSecondName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [secondPassword, setSecondPassword] = useState<string>('');
  const [marketing, setMarketing] = useState<boolean>(true);
  const [terms, setTerms] = useState<boolean>(false);

  const handleSmartCard = (event: ChangeEvent<HTMLInputElement>) => setSmartCard(event.target.value);
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    const isValid = emailChecker(event.target.value);
    if (!isValid) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
  };
  const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
    if (event.target.value.length >= 2) {
      setFirstNameError(false);
      return;
    }
    setFirstNameError(true);
  };
  const handleSecondNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSecondName(event.target.value);
    if (event.target.value.length >= 2) {
      setSecondNameError(false);
      return;
    }
    setSecondNameError(true);
  };
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);

    if (event.target.value.length < 8) {
      setPasswordError(true);
      return;
    }
    setPasswordError(false);
    if (event.target.value !== secondPassword) {
      setSecondPasswordError(true);
    }
  };
  const handlesecondPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSecondPassword(event.target.value);
    if (event.target.value !== password) {
      setSecondPasswordError(true);
      return;
    }
    setSecondPasswordError(false);
  };

  const handleMarketingChange = (event: ChangeEvent<HTMLInputElement>) => setMarketing(event.target.checked);
  const handleTermsChange = (event: ChangeEvent<HTMLInputElement>) => setTerms(event.target.checked);

  const buttonDisabled = !email.length || !password.length || !firstName.length || !secondName.length || !secondPassword.length || !terms
    || emailError || firstNameError || secondNameError || passwordError || secondPasswordError;

  const handleSignUp = () => {
    const user: Profile = { 
      badges: {}, 
      checked_tutorial: false, 
      daily_counter: 0, 
      daily_counter_limit: false, 
      email, 
      first_name: firstName, 
      has_requests: false, 
      is_sherpa: false, 
      is_staff: false,
      is_csd_representative: false,
      last_name: secondName, 
      marketing, 
      no_sell: false, 
      push: false, 
      smart_card_nfc_id: smartCard,
      username: email, 
      verified: false, 
      verified_sent: false,
      groups: [],
    };

    const deviceInfo: DeviceInfo = {
      os: '',
      version: '',
    };

    setLoading(true);
    API.Users.signUp(user, password, deviceInfo)
      .then(() => {
        setLoading(false);
        Router(navigation).goToEmailVerification(email);
      })
      .catch((error) => {
        setLoading(false);
        if (error.data.error_code) {
          setToastMessage(error.message);
          setToastbarVisible(true);
          setTimeout(() => setToastbarVisible(false), 3000);
          return;
        }
        setToastMessage('Something went wrong, try again later.');
        setToastbarVisible(true);
        setTimeout(() => setToastbarVisible(false), 3000);
      });
  };

  return (
    <NotAuthProvider>
      {isLoading && <Loading isVisible={isLoading} />}
      {isToastbarVisible && <ToastBar isVisible={isToastbarVisible} message={toastMessage} />}
      <img className="signup-bg-image" style={styles.bgImage} src={screenImage} alt="background" />
      <div style={styles.root}>
        <div className="signup-container" style={styles.mainContainer}>
          <Heading title="Sign up" onBack={handleBackPressed} />
          <div className="signup-form" style={styles.form}>
            <Input placeholder="Physical Smart Card (optional)" onError={false} errorMessage="" onChange={handleSmartCard} />
            <Separator size="S" />
            <Input placeholder="Email Address" onError={emailError} errorMessage="Enter a valid email." onChange={handleEmailChange} type="email" />
            <Separator size="S" />
            <Input placeholder="First name" onError={firstNameError} errorMessage="Enter a valid first name." onChange={handleFirstNameChange} />
            <Separator size="S" />
            <Input placeholder="Last name" onError={secondNameError} errorMessage="Enter a valid last name." onChange={handleSecondNameChange} />
            <Separator size="S" />
            <Input placeholder="Password" onError={passwordError} errorMessage="Enter a valid password." onChange={handlePasswordChange} type="password" />
            <Separator size="S" />
            <Input placeholder="Password Confirmation" onError={secondPasswordError} errorMessage="Passwords must match." onChange={handlesecondPasswordChange} type="password" />
            <Separator size="S" />

            <CustomCheckBox title="Yes please. I'd like the occasional update from Olyns." checked={marketing} onChange={handleMarketingChange} />
            <Separator size="S" />
            <CustomCheckBox title="I accept the Terms of Use" checked={terms} onChange={handleTermsChange} />

            <Separator size="M" />
            <Button type={buttonDisabled ? 'DISABLED' : 'PRIMARY'} width="100%" title="Sign me up!" onClick={handleSignUp} disabled={buttonDisabled} />
          </div>
        </div>
      </div>
    </NotAuthProvider>
  );
};
